import { TimelineEntry } from '@blisspointmedia/bpm-types/dist/OfflineInputs';
import { WeekRow } from './data-qa-types';
import { IndicatorReason, ReviewIndicator, ReviewIndicatorType } from './media-inputs-utils';

// A run of zero weeks at either edge of the training window this long or longer counts as
// the variable being cut off there. Shorter runs are common for sporadic variables (an
// email blast that simply didn't fire the first week) and aren't worth triaging.
export const EDGE_ZERO_RUN_WEEKS = 4;

export interface ContextVarCompleteness {
  total: number;
  zero: number;
  pct: number;
  // "<first non-zero week> to <last non-zero week>", or 'none'.
  span: string;
}

export interface ContextVarAnalysis {
  completeness: ContextVarCompleteness;
  indicator: ReviewIndicatorType;
  // Why the indicator isn't clear, for its hover text; empty when it is.
  reasons: string[];
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

// One context variable's weekly series inside the training window (`weeks` already
// fencepost-filtered), checked for what would keep the model from learning from it:
// - no non-zero data at all (needs investigating)
// - the same value every week (worth noting -- no variation to explain anything)
// - cut off at an edge: EDGE_ZERO_RUN_WEEKS+ zero weeks at the start or end of the window
//   (worth noting)
// Zeros in the middle are fine -- sporadic variables are normal.
export const analyzeContextVar = (weeks: WeekRow[], key: string): ContextVarAnalysis => {
  const values = weeks.map(w => w.perContextVar[key] ?? 0);
  const total = values.length;
  const firstNonZero = values.findIndex(v => v !== 0);
  const lastNonZero = values.length - 1 - [...values].reverse().findIndex(v => v !== 0);
  const zero = values.filter(v => v === 0).length;
  const completeness: ContextVarCompleteness = {
    total,
    zero,
    pct: total ? Math.round((zero / total) * 100) : 0,
    span:
      firstNonZero === -1
        ? 'none'
        : `${weeks[firstNonZero].weekIso} to ${weeks[lastNonZero].weekIso}`
  };

  if (total > 0 && firstNonZero === -1) {
    return {
      completeness,
      indicator: ReviewIndicator.INVESTIGATE,
      reasons: ['No data in the modeling range']
    };
  }

  const reasons: string[] = [];
  if (total > 1 && values.every(v => v === values[0])) {
    reasons.push('Same value every week');
  }
  const leadingZeros = firstNonZero;
  const trailingZeros = total - 1 - lastNonZero;
  if (leadingZeros >= EDGE_ZERO_RUN_WEEKS) {
    reasons.push(`Data starts ${plural(leadingZeros, 'week')} into the window`);
  }
  if (trailingZeros >= EDGE_ZERO_RUN_WEEKS) {
    reasons.push(`Data ends ${plural(trailingZeros, 'week')} before the window does`);
  }

  return {
    completeness,
    indicator: reasons.length > 0 ? ReviewIndicator.NOTE : ReviewIndicator.CLEAR,
    reasons
  };
};

// The combined outlier flags (one-off calendar events, zero almost every week), checked
// for what would keep the model from estimating them -- not the per-variable checks above,
// which every one-off flag would trip:
// - a flag that never fires in the modeling range (needs investigating): an all-zero
//   input the model can't estimate, e.g. a 2022 flag left in after the window moved on
// - flags active in exactly the same periods (worth noting): the model can't tell their
//   effects apart
export const analyzeOutlierFlags = (
  weeks: WeekRow[],
  flags: { key: string; label: string }[]
): IndicatorReason[] => {
  const activePeriods = (key: string) =>
    weeks.flatMap((w, i) => ((w.perContextVar[key] ?? 0) !== 0 ? [i] : [])).join(',');
  const reasons: IndicatorReason[] = [];

  const silent = flags.filter(flag => activePeriods(flag.key) === '');
  if (weeks.length > 0 && silent.length > 0) {
    reasons.push({
      indicator: ReviewIndicator.INVESTIGATE,
      text: `Never fires in the modeling range: ${silent.map(f => f.label).join(', ')}`
    });
  }

  const byPattern = new Map<string, string[]>();
  flags.forEach(flag => {
    const pattern = activePeriods(flag.key);
    if (pattern !== '') {
      byPattern.set(pattern, [...(byPattern.get(pattern) ?? []), flag.label]);
    }
  });
  byPattern.forEach(labels => {
    if (labels.length > 1) {
      reasons.push({
        indicator: ReviewIndicator.NOTE,
        text: `Active in the same weeks: ${labels.join(', ')}`
      });
    }
  });

  return reasons;
};

// The Promo Flag is built from the promo periods encoded on Offline Inputs, so its card
// can also show them as a calendar to check the series against. Spelled "promo__flag"
// (away, bulova) or "promo_flag" (dsw) in real configs.
export const isPromoFlagVar = (key: string): boolean =>
  key.replace(/_/g, '').toLowerCase() === 'promoflag';

// Offline Inputs promo rows saved without a promo period ID come back merged into one
// entry with no ID (and often no name) -- away has 189 such days, its outlier events,
// spanning 2022-2025. The timeline chart keys its rows by ID, so an entry without one gets
// no label row and every bar after it shifts onto the next promo's label. Gives each such
// entry an ID of its own and a visible name, so each bar stays on its own row.
export const labelUnidentifiedPromos = (entries: TimelineEntry[]): TimelineEntry[] =>
  entries.map((entry, index) => ({
    ...entry,
    promoPeriodId: entry.promoPeriodId || `no-promo-period-id-${index}`,
    promoName: entry.promoName || '(No name)'
  }));
